import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, HostListener } from '@angular/core';
import { TableDataSource } from '../../shared/editable-mat-table/table-data-source';
import { ValidatorService } from '../../shared/editable-mat-table/validator.service';
import { BusinessHoursValidatorService } from './business-hours-validator.service';
import { BusinessHours } from '../models/business-hours.model';
import { Business } from '../models/business.model';
import { BusinessHoursService } from '../services/business-hours.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { TableElement } from '../../shared/editable-mat-table/table-element';

@Component({
  selector: 'app-business-hours-table',
  templateUrl: './business-hours-table.component.html',
  providers: [{ provide: ValidatorService, useClass: BusinessHoursValidatorService }],
  styleUrls: ['./business-hours-table.component.scss']
})
export class BusinessHoursTableComponent implements OnInit, OnDestroy {

  @Input() business: Business;
  @Output() businessHoursChange = new EventEmitter<BusinessHours[]>();

  daysOfTheWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  displayCols = ['fromMmDd', 'toMmDd', 'fromWeekDay', 'toWeekDay', 'fromTime', 'toTime', 'actionsCol'];
  dataSource: TableDataSource<BusinessHours>;

  constructor(private hoursValidator: BusinessHoursValidatorService,
    private _hoursService: BusinessHoursService,
    private _businessManagerService: BusinessManagerService) { }

  async ngOnInit() {
    this.setBusinessHours();
  }

  async ngOnDestroy() {
    this.dataSource.datasourceSubject.unsubscribe();
  }

  addNewRow() {
    this.dataSource.createNew();
  }

  startEdit(row: TableElement<BusinessHours>) {
    row.startEdit();
  }

  async confirmEditOrCreate(row: TableElement<BusinessHours>) {
    if (row.validator.valid) {
      if (row.shouldCreate) {
        this.createBusinessHours(this.business.id, row.currentData);
      } else {
        this.updateBusinessHours(this.business.id, row.currentData);
      }
    }
    row.confirmEditCreate();
  }

  async cancelOrDelete(row: TableElement<BusinessHours>) {
    if (row.shouldDelete) {
      this.deleteBusinessHour(row.currentData);
    }
    row.cancelOrDelete();
  }

  //#region Service Calls
  async createBusinessHours(businessId: number, bHrs: BusinessHours) {
    const bHours = await this._hoursService.createBusinessHours(businessId, bHrs);
    if (!this.business.hours) { // Previously the business never had custom hours
      this.business.hours = new Array<BusinessHours>();
    }
    this.business.hours.push(bHours);
    await this.updateTheBusiness();
  }

  async updateBusinessHours(businessId: number, hours: BusinessHours) {
    const hoursId = hours.id;
    this.business.hours.splice(this.business.hours.indexOf(hours), 1);
    await this._hoursService.updateBusinessHours(businessId, hoursId, hours)
      .then(bHours => {
        this.business.hours.push(bHours);
        this.updateTheBusiness();
      });
  }

  async deleteBusinessHour(hours: BusinessHours) {
    if (hours.id) { // id will be undefined if its a newly un-added row aka cancel
      await this._hoursService.deleteBusinessHours(this.business.id, hours.id)
        .then(() => {
          this.business.hours = this.business.hours.filter(bH => bH.id !== hours.id);
          this.updateTheBusiness();
        });
    }
  }

  async updateTheBusiness() {
    const newBusiness = await this._businessManagerService.updateBusiness(this.business);
    Object.assign(this.business, newBusiness);
  }

  setBusinessHours() {
    this.dataSource = new TableDataSource<any>(this.business.hours, BusinessHours, this.hoursValidator);
    this.dataSource.datasourceSubject.subscribe(bHrs => this.businessHoursChange.emit(bHrs));
  }
  //#endregion

  hasUnsavedChanges(): boolean {
    return this.dataSource.hasUnsavedRow();
  }
}
